import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { format, startOfToday } from 'date-fns';
import { toast } from 'react-hot-toast';
import { GET_MY_VACATIONS } from '../../graphql/my-availability-queries';
import { GET_EMPLOYEE_VACATIONS } from '../../graphql/team-availability-queries';
import { DELETE_EMPLOYEE_VACATION } from '../../graphql/team-availability-mutations';
import {
  compareCalendarDates,
  toCalendarDateKey,
  toLocalCalendarDate,
} from '../../utils/availabilityDateUtils';
import VacationApprovalBadge from './VacationApprovalBadge';

const VACATION_TYPE_LABELS = {
  PTO: 'PTO',
  SICK: 'Sick',
  PERSONAL: 'Personal',
  UNPAID: 'Unpaid',
  OTHER: 'Other',
};

const formatVacationDates = (vacation) => {
  const start = toLocalCalendarDate(vacation.startDate);
  const end = toLocalCalendarDate(vacation.endDate);
  const isSameDay = toCalendarDateKey(start) === toCalendarDateKey(end);

  return isSameDay
    ? format(start, 'EEE, MMM d, yyyy')
    : `${format(start, 'EEE, MMM d')} – ${format(end, 'EEE, MMM d, yyyy')}`;
};

function PastTimeOffCard({ userId = null, refreshKey = 0, canDelete = false, onChange }) {
  const isEmployeeView = Boolean(userId);
  const previousRefreshKey = useRef(refreshKey);
  const [deletingId, setDeletingId] = useState(null);
  const query = isEmployeeView ? GET_EMPLOYEE_VACATIONS : GET_MY_VACATIONS;
  const { data, loading, error, refetch } = useQuery(query, {
    variables: isEmployeeView ? { userId } : undefined,
    skip: isEmployeeView && !userId,
    fetchPolicy: 'network-only',
  });
  const [deleteEmployeeVacation] = useMutation(DELETE_EMPLOYEE_VACATION, {
    onCompleted: () => {
      toast.success('Past time off deleted');
      setDeletingId(null);
      refetch();
      onChange?.();
    },
    onError: (mutationError) => {
      toast.error(mutationError.message || 'Failed to delete past time off');
      setDeletingId(null);
    },
  });

  useEffect(() => {
    if (refreshKey !== previousRefreshKey.current) {
      previousRefreshKey.current = refreshKey;
      refetch();
    }
  }, [refreshKey, refetch]);

  const groupedPastTimeOff = useMemo(() => {
    const vacations = isEmployeeView
      ? data?.employeeVacations || []
      : data?.myVacations || [];
    const todayKey = toCalendarDateKey(startOfToday());
    const pastVacations = vacations
      .filter((vacation) => toCalendarDateKey(vacation.endDate) < todayKey)
      .sort((left, right) => {
        const endComparison = compareCalendarDates(right.endDate, left.endDate);
        return endComparison || compareCalendarDates(right.startDate, left.startDate);
      });

    return pastVacations.reduce((groups, vacation) => {
      const endDate = toLocalCalendarDate(vacation.endDate);
      const year = endDate.getFullYear();
      const existingGroup = groups.find((group) => group.year === year);

      if (existingGroup) {
        existingGroup.vacations.push(vacation);
      } else {
        groups.push({ year, vacations: [vacation] });
      }

      return groups;
    }, []);
  }, [data, isEmployeeView]);

  const handleDelete = (vacation) => {
    if (!canDelete || !isEmployeeView) return;
    const dateLabel = formatVacationDates(vacation);
    const confirmed = window.confirm(
      `Permanently delete the time-off entry for ${dateLabel}? This can change the employee's PTO balance and cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(vacation.id);
    deleteEmployeeVacation({ variables: { id: vacation.id } });
  };

  return (
    <section className="overflow-hidden rounded-lg bg-white shadow">
      <div className="border-b border-gray-200 px-6 py-5">
        <h2 className="text-lg font-semibold text-gray-900">Past Time Off</h2>
        <p className="mt-1 text-sm text-gray-600">
          Previous time-off entries are listed newest first and grouped by year.
          {canDelete && ' Administrators can permanently remove incorrect or test entries.'}
        </p>
      </div>

      {loading ? (
        <div className="px-6 py-10 text-center text-gray-500">Loading past time off...</div>
      ) : error ? (
        <div className="m-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Unable to load past time off: {error.message}
        </div>
      ) : groupedPastTimeOff.length === 0 ? (
        <div className="px-6 py-10 text-center text-gray-500">No past time off recorded.</div>
      ) : (
        <div className="max-h-[32rem] overflow-y-auto">
          {groupedPastTimeOff.map((group) => (
            <div key={group.year}>
              <div className="sticky top-0 z-10 flex items-center justify-between border-y border-gray-200 bg-gray-50 px-6 py-2.5 first:border-t-0">
                <h3 className="text-sm font-semibold text-gray-800">{group.year}</h3>
                <span className="text-xs text-gray-500">
                  {group.vacations.length} {group.vacations.length === 1 ? 'entry' : 'entries'}
                </span>
              </div>
              <ul className="divide-y divide-gray-200 px-6">
                {group.vacations.map((vacation) => {
                  const typeLabel = VACATION_TYPE_LABELS[vacation.vacationType]
                    || vacation.vacationType
                    || 'Time Off';

                  return (
                    <li key={vacation.id} className="py-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {formatVacationDates(vacation)}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <span className="inline-flex rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                              {typeLabel}
                            </span>
                            <VacationApprovalBadge status={vacation.approvalStatus} />
                          </div>
                          {vacation.reason && (
                            <p className="mt-2 text-sm text-gray-600">{vacation.reason}</p>
                          )}
                          {vacation.reviewNote && (
                            <p className="mt-1 text-xs text-gray-500">
                              Administrator note: {vacation.reviewNote}
                            </p>
                          )}
                        </div>
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => handleDelete(vacation)}
                            disabled={deletingId !== null}
                            className="self-start rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {deletingId === vacation.id ? 'Deleting...' : 'Delete'}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default PastTimeOffCard;
